import React, { useEffect, useState } from 'react';
import { post } from '../httpService';
import { formatDateTime } from '../utils/util_function';
import { useForm } from 'react-hook-form';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
);

const Transaction: React.FC = () => {
    const [transactionData, setTransactionData] = useState<any>([]);
    const [isLoad, setIsLoad] = useState<boolean>(true);
    const [expandedRow, setExpandedRow] = useState<string | null>(null);
    const [balance, setBalance] = useState<any>(null);
    const [cashOutIsLoad, setCashOutIsLoad] = useState<boolean>(false);

    const { register, handleSubmit, reset, formState: { isSubmitting } } = useForm({
        defaultValues: {
            withdrawAmount: '',
            accountNumber: '',
            operator: ''
        }
    });

    const getBalance = () => {
        post('/balance', null)
            .then((response) => {
                setBalance(response.data.amount);
            })
            .catch((error) => {
                console.error('Erreur GET solde :', error);
            });
    }

    const getTransaction = () => {
        post('/transaction-list', null)
            .then((response) => {
                setTransactionData(response.data.data['data']);
                setIsLoad(false);
            })
            .catch((error) => {
                console.error('Erreur GET :', error);
            });
    }

    useEffect(() => {
        getBalance();
        getTransaction();
    }, []);

    const toggleRowExpansion = (groupId: string) => {
        if (expandedRow === groupId) {
            setExpandedRow(null);
        } else {
            setExpandedRow(groupId);
        }
    };

    const cancel = () => {
        reset();
    };

    const onSubmit = (data: any) => {
        const payload = {
            account_number: data.accountNumber,
            operator: data.operator,
            bank_uuid: data.accountNumber,
            balance_amount: data.withdrawAmount,
            is_business: true,
            rT: data.accountNumber,
            rMt: data.withdrawAmount,
            rMo: data.operator === "Orange Money" ? 2 : 1,
            rOTP: 1234,
        };

        setCashOutIsLoad(true);

        post('/withdraw-balance', payload)
            .then((response) => {
                getBalance();
                getTransaction();
                setCashOutIsLoad(false);
                reset();
            })
            .catch((error) => {
                setCashOutIsLoad(false);
                console.error('Erreur retrait :', error);
            });
    };

    const transactionDataForLineChart = {
        labels: transactionData.map((t: any) => formatDateTime(t.created_at)),
        datasets: [
            {
                label: 'Montant des transactions',
                data: transactionData.map((t: any) => t.amount),
                fill: false,
                borderColor: 'rgb(75, 192, 192)',
                tension: 0.1
            }
        ]
    };

    return (
        <div className="p-5">
            <div className="card border p-5 mb-10 rounded border-grey bg-white">
                <h1>Accueil / transactions / liste</h1>
                <h1 className="text-lg">Liste des transactions</h1>
            </div>

            <div className="lg:flex lg:space-x-2">
                <div className="lg:w-2/3 card border p-5 rounded border-grey bg-white">
                    <h1 className="text-lg font-bold">Solde</h1>
                    <p className="text-2xl">{balance != null ? balance + " FCFA" : "***"} </p>
                    <hr className='my-4 text-gray' />
                    <div className="bg-white p-5 rounded border-grey border shadow-sm w-full">
                        <h2 className="text-lg mb-4">Effectuer un retrait</h2>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <div className="mb-4">
                                <label className="block mb-1">Montant</label>
                                <input
                                    type="text"
                                    className="w-full p-2 border border-gray-light bg-grey10 rounded"
                                    {...register('withdrawAmount')}
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block mb-2">Opérateur</label>
                                <div className="flex items-center">
                                    <input
                                        type="radio"
                                        id="orangeMoney"
                                        value="Orange Money"
                                        {...register('operator')}
                                        className="mr-2"
                                    />
                                    <label htmlFor="orangeMoney" className="mr-4">Orange Money</label>
                                    <input
                                        type="radio"
                                        id="mtnMoney"
                                        value="MTN Money"
                                        {...register('operator')}
                                        className="mr-2"
                                    />
                                    <label htmlFor="mtnMoney">MTN Money</label>
                                </div>
                            </div>
                            <div className="mb-4">
                                <label className="block mb-1">Numéro de compte (téléphone)</label>
                                <input
                                    type="text"
                                    className="w-full p-2 border border-gray-light bg-grey10 rounded"
                                    {...register('accountNumber')}
                                />
                            </div>

                            <div className="flex justify-end">
                                <button
                                    type="button"
                                    className="px-4 py-2 bg-danger text-white rounded-full mr-2"
                                    onClick={cancel}
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-primary text-white rounded-full"
                                    disabled={isSubmitting || cashOutIsLoad}
                                >
                                    {cashOutIsLoad ? "Traitement..." : "Transférer"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
                <div className="mt-4 lg:mt-0 lg:w-full card border p-5 rounded border-grey bg-white">
                    <h1 className="text-lg font-bold mb-5">Graphique des transactions</h1>
                    <Line data={transactionDataForLineChart} />
                </div>
            </div>

            <div className='mt-2 card bg-white p-2 rounded overflow-scroll'>
                <h1 className="text-lg font-bold py-5">Graphique des transactions</h1>
                <table className="table-auto w-full">
                    <thead>
                        <tr className='text-left border border-grey text-sm text-bold bg-grey10'>
                            <th className='text-center'>N°</th>
                            <th>Date</th>
                            <th>ID transaction</th>
                            <th>Type de Transaction</th>
                            <th>Montant</th>
                            <th>Frais</th>
                            <th>Net</th>
                            <th>Détails</th>
                            <th>Statut</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody className="bg-white">
                        {!isLoad && <>
                            {transactionData.length > 0 && transactionData.map((transactions: any, index: any) =>
                                <React.Fragment key={transactions.id}>
                                    <tr className={`border border-grey text-sm px-4 ${expandedRow === transactions.id ? 'bg-more-bg font-bold' : ''}`} onClick={() => toggleRowExpansion(transactions.id)}>
                                        <td className='text-center'>{index}</td>
                                        <td>{formatDateTime(transactions.created_at)}</td>
                                        <td>{transactions.transection_id}</td>
                                        <td>{transactions.operation_type.toLowerCase() === "paid" ? "transfer" : transactions.operation_type.toLowerCase()}</td>
                                        <td>{transactions.amount} </td>
                                        <td>{transactions.transection_type.toLowerCase() === "business" && transactions.fees != null ? transactions.fees : "--"} </td>
                                        <td>{transactions.transection_type.toLowerCase() === "business" && transactions.fees != null ? (transactions.amount - transactions.fees) : transactions.amount} </td>
                                        <td>{transactions.note}</td>
                                        <td className={transactions.status.toLowerCase() === "pending" ? "text-orange" : transactions.status.toLowerCase() === "processed" ? "text-green" : "text-danger"}>{transactions.status.toLowerCase()}</td>
                                    </tr>
                                </React.Fragment>
                            )}
                        </>}

                        {isLoad && <>
                            <tr><td colSpan={12} className="text-center"></td></tr>
                            <tr><td colSpan={12} className="text-center">Loading...</td></tr>
                            <tr><td colSpan={12} className="text-center"></td></tr>
                        </>}

                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default Transaction;
